/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { getUrlSync, isOboRequest } from '@msdyn365-commerce/core';
import {
    AttributeTextValue,
    BusinessPartnerOperationDeliveryType,
    BusinessPartnerUsersDataActions,
    Cart,
    InvoiceSearchCriteria,
    InvoiceType,
    IQueryResultSettings,
    OrderInvoice
} from '@msdyn365-commerce/retail-proxy';
import { getCurrentAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import {
    ArrayExtensions,
    getCustomer,
    GetCustomerInput,
    // getInvoices,
    IQueryResultSettingsModuleConfig,
    QueryResultSettingsProxy,
    StringExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import { Alert, Button, Heading, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import getInvoiceBreakdownAction, { GetInvoiceBreakdownInput } from '../../actions/get-invoice-breakdown.action';
import { InvoiceErrorComponent } from './components/invoice-error-component';
import { InvoicesFilterComponent, InvoicesFilterState } from './components/invoices-filter-component';
import { InvoicesTableComponent } from './components/invoices-table/invoices-table-component';
import { IMakePaymentResources, MakePaymentComponent } from './components/make-payment-component';
import { RequestInvoiceButton } from './components/request-invoice-button';
import { RequestInvoiceModal } from './components/request-invoice-modal';
import { InvoiceModel } from './helpers/invoice-data';
import { IAddInvoicesToCartFailureResult } from './helpers/pay-invoices';
import { IGdnInvoicesListData } from './gdn-invoices-list.data';
import { IGdnInvoicesListProps } from './gdn-invoices-list.props.autogenerated';
import getGdnInvoices, { GetGdnInvoicesInput } from '../../actions/get-gdn-invoices.action';
import {
    IGdnPastDueInvoiceDetailsEntity,
    IGetGdnOpenInvoiceDetailsResponse,
    IInvoicePaymentScheduleEntity
} from '../../actions/DataServiceEntities.g';
import {
    addInvoicesToCartAsync,
    getGdnOpenInvoiceDetails_V3Async,
    getGdnPastDueInvoiceDetailsAsync
} from '../../actions/DataActionExtension.g';
import { InvoiceTile } from './components';
import InvoicePaymentTotals from './components/invoie-payment-totals.component';
import { InvoicePaidStatus } from './helpers';

export type InvoicesListStatus = 'LOADING' | 'SUCCESS' | 'FAILED' | 'EMPTY';
export type InvoiceErrorHost = 'ADDINVOICETOCART';

export interface IInvoicesListViewProps extends IGdnInvoicesListProps<IGdnInvoicesListData> {
    moduleProps: IModuleProps;

    header: JSX.Element;
    filter: JSX.Element;
    content?: JSX.Element;
    invoiceRequestModal?: JSX.Element;
    pagination?: JSX.Element;
}

export interface IInvoiceErrorState {
    errorHost?: InvoiceErrorHost;
    errorReason?: IAddInvoicesToCartFailureResult;
    errorMessage: string;
    otherError?: string;
    errorCode?: string;
}

export interface IInvoicesListState {
    invoices: InvoiceModel[];
    invoicesFilterState: InvoicesFilterState;
    errorState: IInvoiceErrorState;
    isInvoiceRequestModalOpen: boolean;
    isLoading: boolean;
    isOpen: boolean;
    isPageLoaded: boolean;
    payInvoiceErrorModalOpen: boolean;
    selectedButtonElement?: HTMLButtonElement | null;
    invoiceBreakdownList: IInvoicePaymentScheduleEntity[];
    invoicePaymentTotals: IGetGdnOpenInvoiceDetailsResponse | null;
    pastDuePageNumber: number;
    pastDuePageSize: number;
    hasMorePastDueInvoices: boolean;
}

/**
 * InvoicesList component.
 * @extends {React.Component<IGdnInvoicesListProps<IGdnInvoicesListData>, IInvoicesListState>}
 */
@observer
class InvoicesList extends React.Component<IGdnInvoicesListProps<IGdnInvoicesListData>, IInvoicesListState> {
    private readonly getInvoiceType = this.props.config?.invoiceTypes || [];

    private readonly _className: string = 'msc-invoices-list';

    private readonly payInvoiceRequestReference: React.RefObject<HTMLButtonElement>;

    private readonly makePaymentReference: React.RefObject<HTMLButtonElement>;

    private buttonChildReference: React.RefObject<HTMLButtonElement>[] = [];

    private buttonChildPayButtonReference: React.RefObject<HTMLButtonElement>[] = [];

    private readonly _queryResultSettings: IQueryResultSettings;

    private invoiceIds: string[] = [];

    constructor(props: IGdnInvoicesListProps<IGdnInvoicesListData>) {
        super(props);

        this._queryResultSettings = QueryResultSettingsProxy.fromModuleData(
            this.props.context.request,
            this.props.config as IQueryResultSettingsModuleConfig
        ).QueryResultSettings;
        this.payInvoiceRequestReference = React.createRef<HTMLButtonElement>();
        this.makePaymentReference = React.createRef<HTMLButtonElement>();
        this._updateInvoicesState = this._updateInvoicesState.bind(this);
        this._onAddToCartFailed = this._onAddToCartFailed.bind(this);
        this._onToggleInvoiceRequestModal = this._onToggleInvoiceRequestModal.bind(this);
        this._handleFilterStateChange = this._handleFilterStateChange.bind(this);
        this._loadMoreInvoices = this._loadMoreInvoices.bind(this);
        this._requestInvoice = this._requestInvoice.bind(this);
        this._requestSpecificInvoice = this._requestSpecificInvoice.bind(this);
        this._setPayInvoiceErrorModalClose = this._setPayInvoiceErrorModalClose.bind(this);
        this._addInvoiceToCart = this._addInvoiceToCart.bind(this);
        this._addAllowableFreightAmountToCart = this._addAllowableFreightAmountToCart.bind(this);
        this.state = {
            invoices: [],
            invoicesFilterState: InvoicesFilterState.ShowAll,
            isLoading: true,
            isOpen: true,
            isPageLoaded: false,
            errorState: { errorMessage: '' },
            isInvoiceRequestModalOpen: false,
            payInvoiceErrorModalOpen: false,
            selectedButtonElement: null,
            invoiceBreakdownList: [],
            invoicePaymentTotals: null,
            pastDuePageNumber: 1,
            pastDuePageSize: this.props.config.itemsPerPage || 15,
            hasMorePastDueInvoices: true
        };
    }

    public shouldComponentUpdate(nextProps: IGdnInvoicesListProps<IGdnInvoicesListData>, nextState: IInvoicesListState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const {
            config: { className = '', heading },
            resources: { invoicesListTitle }
        } = this.props;

        const invoicesListTitleHeading = heading?.text ?? invoicesListTitle;

        const headerClassName = `${this._className}__header`;

        const header = (
            <div className={headerClassName}>
                <Heading className={`${headerClassName}-heading`} text={invoicesListTitleHeading} />
                <div className={`${headerClassName}-description`}>
                    {/* <div className={`${headerClassName}-description-title`}>{invoicesListDescTitle}</div> */}
                    {/* <div className={`${headerClassName}-description-amount`}>{amountText}</div>
                    <div className={`${headerClassName}-description-count`}>{countText}</div> */}
                </div>
            </div>
        );

        const viewProps: IInvoicesListViewProps = {
            ...(this.props as IGdnInvoicesListProps<IGdnInvoicesListData>),
            moduleProps: {
                moduleProps: this.props,
                className: classnames(this._className, className)
            },
            header,
            invoiceRequestModal: this._renderInvoiceRequestModal(),
            filter: this._renderInvoicesFilter(this._className),
            pagination: this._renderPagination()
        };

        if (!this.state.isPageLoaded) {
            return this.props.renderView(viewProps);
        }
        const renderListView = this.props.config.renderListView;

        if (ArrayExtensions.hasElements(this.state.invoices)) {
            if (renderListView) {
                viewProps.content = this._renderInvoiceTileView();
            } else {
                viewProps.content = this._renderListContainer();
            }
        } else {
            viewProps.content = this._renderEmptyInvoiceLinesPageContent();
        }

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public async componentDidMount(): Promise<void> {
        await this.props.data.customer;
        const invoicesData = await this.props.data.invoices;
        const shouldDisplayInvoiceTotals = this.props.config.displayInvoicePaymentTotals || false;
        const isCreditNotesIncluded: boolean = this.getInvoiceType.some(inv => inv.invoiceType === 'CreditNoteInvoice');
        const isFreeTextCreditNotesIncluded: boolean = this.getInvoiceType.some(inv => inv.invoiceType === 'FreeTextCreditNoteInvoice');
        const isProjectCreditNotesIncluded: boolean = this.getInvoiceType.some(inv => inv.invoiceType === 'ProjectCreditNoteInvoice');

        if (shouldDisplayInvoiceTotals) {
            const resp = await getGdnOpenInvoiceDetails_V3Async(
                { callerContext: this.props.context.actionContext },
                isCreditNotesIncluded,
                isFreeTextCreditNotesIncluded,
                isProjectCreditNotesIncluded
            );
            this.setState({ invoicePaymentTotals: resp });
        }

        void this._initializeWithInvoices(invoicesData);

        reaction(
            () => this.state.isInvoiceRequestModalOpen,
            async () => {
                if (this.invoiceIds && !this.state.isInvoiceRequestModalOpen) {
                    const context = { callerContext: this.props.context.actionContext };
                    const b2bUserId: string = this.props.data.b2bUserInformation?.result?.B2BUserId || '';
                    for (const invoiceId of this.invoiceIds) {
                        await BusinessPartnerUsersDataActions.requestInvoiceCopyAsync(
                            context,
                            b2bUserId,
                            invoiceId,
                            BusinessPartnerOperationDeliveryType.Email
                        );
                    }
                    this.invoiceIds = [];
                }
            }
        );
    }

    private _addInvoiceToCart = async (
        invoiceId: string,
        amount: number,
        allowableFreightAmount: number,
        isAllowableRowSelected: boolean,
        amountDue: number
    ) => {
        let updateIdx = 1;
        const {
            context: { actionContext },
            data: { cart }
        } = this.props;

        void (await addInvoicesToCartAsync(
            { callerContext: actionContext },
            [{ Amount: amount, InvoiceId: invoiceId }],
            cart?.result?.cart.Id || ''
        ));
        await cart?.result?.refreshCart({});
        updateIdx++;
        if (updateIdx === 2 && allowableFreightAmount > 0) {
            await this._addAllowableFreightAmountToCart(invoiceId, allowableFreightAmount, isAllowableRowSelected, amount);
            updateIdx++;
        }
        if (updateIdx === 3) {
            await this._addAmountDueToCart(invoiceId, amountDue);
        }
        document.dispatchEvent(new CustomEvent('INVOICE_ADDED'));
    };

    private async _initializeWithInvoices(invoicesData: OrderInvoice[]): Promise<void> {
        const allInvoices = invoicesData.map((invoice, index) => {
            this.buttonChildReference[Number(index)] = React.createRef<HTMLButtonElement>();
            this.buttonChildPayButtonReference[Number(index)] = React.createRef<HTMLButtonElement>();
            return new InvoiceModel(invoice);
        });
        const invoiceBreakdownList: IInvoicePaymentScheduleEntity[] = [];
        if (this.props.config.renderListView) {
            const invoicesToLoad = invoicesData.filter(
                inv => !this.state.invoiceBreakdownList.some(breakdown => breakdown.Invoice === inv.Id)
            );
            if (!this.state.isPageLoaded) {
                // initial load
                const ids = allInvoices.map(inv => inv.id);
                const input = new GetInvoiceBreakdownInput(ids);
                const invoiceBreakdownListResponse = await getInvoiceBreakdownAction(input, this.props.context.actionContext);
                invoiceBreakdownList.push(...invoiceBreakdownListResponse.invoiceBreakdownList);
            } else if (this.state.isPageLoaded && invoicesToLoad.length > 0) {
                const invoicesNotInState = invoicesToLoad.map((invoice, index) => {
                    this.buttonChildReference[Number(index)] = React.createRef<HTMLButtonElement>();
                    this.buttonChildPayButtonReference[Number(index)] = React.createRef<HTMLButtonElement>();
                    return new InvoiceModel(invoice);
                });
                const ids = invoicesNotInState.map(inv => inv.id);
                const input = new GetInvoiceBreakdownInput(ids);
                const invoiceBreakdownListResponse = await getInvoiceBreakdownAction(input, this.props.context.actionContext);
                invoiceBreakdownList.push(...invoiceBreakdownListResponse.invoiceBreakdownList);
            }
        }
        this.setState(prev => ({
            invoices: allInvoices,
            isLoading: false,
            isPageLoaded: true,
            invoiceBreakdownList: [...prev.invoiceBreakdownList, ...invoiceBreakdownList]
        }));
    }

    private _addInvoices(invoicesData: OrderInvoice[]): void {
        const newInvoices = invoicesData.map(invoice => new InvoiceModel(invoice));
        const invoices = this.state.invoices.concat(newInvoices);
        this.setState({
            invoices,
            isLoading: false
        });
    }

    private _renderEmptyInvoiceLinesPageContent(): JSX.Element {
        const containerClass = `${this._className}__empty-lines__container`;
        const actionsContainerClass = `${containerClass}__actions`;
        return (
            <div className={containerClass}>
                <div className={`${containerClass}__title`}>{this.props.resources.invoiceEmptyInvoiceLinesTitle}</div>
                <div className={`${containerClass}__description`}>{this.props.resources.invoiceEmptyInvoiceLinesDescription}</div>
                <div className={actionsContainerClass}>
                    <Button
                        className={`${actionsContainerClass}__continue-shopping`}
                        aria-label={this.props.resources.invoiceEmptyInvoiceLinesContinueShoppingButtonAriaLabel}
                        href={getUrlSync('home', this.props.context.actionContext)}
                    >
                        {this.props.resources.invoiceEmptyInvoiceLinesContinueShoppingButtonText}
                    </Button>
                </div>
            </div>
        );
    }

    private _getBreakdownForInvoice(invoice: InvoiceModel): IInvoicePaymentScheduleEntity[] {
        return this.state.invoiceBreakdownList.filter(inv => inv.Invoice === invoice.id);
    }

    private _renderInvoiceTileView(): JSX.Element {
        const { invoices } = this.state;
        const containerClass = `${this._className}__container`;
        const containerHeaderClass = `${containerClass}__header`;
        return (
            <>
                <div className={containerHeaderClass}> {this._renderInvoiceRequestButton()} </div>
                <div className={'invoice-listview'}>
                    {invoices.map(inv => (
                        <InvoiceTile
                            addInvoiceToCart={this._addInvoiceToCart}
                            cultureFormatter={this.props.context.cultureFormatter}
                            invoice={inv}
                            invoiceBreakdownList={this._getBreakdownForInvoice(inv)}
                            key={inv.id}
                            actionContext={this.props.context.actionContext}
                            resources={this.props.resources}
                            cart={this.props.data.cart?.result?.cart}
                            // addAllowableFreightAmountToCart={this._addAllowableFreightAmountToCart}
                            allowMultipleInvoicePayment={this.props.config.allowMultipleInvoicePayment}
                            paymentAddedMessage={this.props.config.paymentAddedMessage}
                            paymentPendingMessage={
                                this.props.config.paymentPendingMessage ||
                                'Payment pending. Additional payments can be made when payment has been posted.'
                            }
                        />
                    ))}
                </div>
            </>
        );
    }

    private _addAmountDueToCart = async (invoiceId: string, amountDue: number) => {
        const cart: Cart = Object.assign({}, (await this.props.data.cart)?.cart);
        let attributeArr: AttributeTextValue[] = cart.AttributeValues || [];
        const invoiceAmountDueIdx = attributeArr.findIndex(attribute => attribute.Name === 'InvoiceAmountDue');

        let invoiceAmountDueData: { invoiceId: string; amountDue: number }[] = [];

        if (invoiceAmountDueIdx !== -1) {
            try {
                invoiceAmountDueData = JSON.parse(attributeArr[invoiceAmountDueIdx].TextValue || '[]');
            } catch {
                invoiceAmountDueData = [];
            }
        }
        const existingIndex = invoiceAmountDueData.findIndex(item => item.invoiceId === invoiceId);

        let isInvoiceAmountDueUpdated = false;
        if (existingIndex !== -1) {
            if (invoiceAmountDueData[existingIndex].amountDue !== amountDue) {
                invoiceAmountDueData[existingIndex].amountDue = amountDue;
                isInvoiceAmountDueUpdated = true;
            } else {
                return;
            }
        } else {
            invoiceAmountDueData.push({ invoiceId, amountDue: amountDue });
            isInvoiceAmountDueUpdated = true;
        }

        if (isInvoiceAmountDueUpdated) {
            const serializedInvoiceAmountDueValue = JSON.stringify(invoiceAmountDueData);

            const invoiceAmountDueAttribute = {
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                Name: 'InvoiceAmountDue',
                ExtensionProperties: [],
                TextValue: serializedInvoiceAmountDueValue,
                TextValueTranslations: []
            };

            if (invoiceAmountDueIdx !== -1) {
                attributeArr.splice(invoiceAmountDueIdx, 1, invoiceAmountDueAttribute);
            } else {
                attributeArr.push(invoiceAmountDueAttribute);
            }
        }
        cart.AttributeValues = attributeArr;
        await (await this.props.data.cart)?.updateCart({ newCartObject: cart });
    };

    private _addAllowableFreightAmountToCart = async (invoiceId: string, amount: number, isSelected: boolean, invoiceAmountDue: number) => {
        const cart: Cart = Object.assign({}, (await this.props.data.cart)?.cart);
        let attributeArr: AttributeTextValue[] = cart.AttributeValues || [];

        const allowableFreightIdx = attributeArr.findIndex(attribute => attribute.Name === 'AllowableFreightAmount');
        let freightData: { invoiceId: string; amount: number }[] = [];

        if (allowableFreightIdx !== -1) {
            try {
                freightData = JSON.parse(attributeArr[allowableFreightIdx].TextValue || '[]');
            } catch {
                freightData = [];
            }

            if (isSelected && freightData.some(item => item.invoiceId === invoiceId)) {
                return;
            }
        }

        let isCartUpdated = false;
        if (isSelected) {
            const existingIndex = freightData.findIndex(item => item.invoiceId === invoiceId);
            if (existingIndex !== -1) {
                freightData[existingIndex].amount = amount;
            } else {
                freightData.push({ invoiceId, amount });
            }
            isCartUpdated = true;
        } else {
            const prevLength = freightData.length;
            freightData = freightData.filter(item => item.invoiceId !== invoiceId);
            isCartUpdated = prevLength !== freightData.length;
        }

        if (isCartUpdated) {
            const serializedFreightValue = JSON.stringify(freightData);

            const allowableFreightAttribute = {
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                Name: 'AllowableFreightAmount',
                ExtensionProperties: [],
                TextValue: serializedFreightValue,
                TextValueTranslations: []
            };

            if (allowableFreightIdx !== -1) {
                attributeArr.splice(allowableFreightIdx, 1, allowableFreightAttribute);
            } else {
                attributeArr.push(allowableFreightAttribute);
            }
        }
        cart.AttributeValues = attributeArr;
        await (await this.props.data.cart)?.updateCart({ newCartObject: cart });
    };

    private _renderListContainer(): JSX.Element {
        const containerClass = `${this._className}__container`;
        const containerHeaderClass = `${containerClass}__header`;
        const containerContentClass = `${containerClass}__content`;
        const isOnBehalfOfRequest = isOboRequest(this.props.context.request);

        return (
            <div className={containerClass}>
                <div className={containerHeaderClass}>
                    {this._renderMakePayment(containerHeaderClass, isOnBehalfOfRequest)}
                    {this._renderInvoiceRequestButton()}
                </div>
                <div className={containerContentClass}>{this._renderInvoiceErrors()}</div>
                <div className={containerContentClass}>{this._renderInvoicesList(containerContentClass)}</div>
            </div>
        );
    }

    private _updateInvoicesState(): void {
        this.setState({
            invoices: this.state.invoices
        });
    }

    private async _getInvoicesSearchCriteria(state: InvoicesFilterState): Promise<InvoiceSearchCriteria> {
        const searchCriteria: InvoiceSearchCriteria = {};

        const context = this.props.context.actionContext;

        const businessPartner = await getCurrentAsync({ callerContext: context });
        searchCriteria.BusinessPartnerId = businessPartner.BusinessPartnerId;

        const customer = await getCustomer(new GetCustomerInput(context.requestContext.apiSettings), context);
        searchCriteria.CustomerId = customer.AccountNumber;

        switch (state) {
            case InvoicesFilterState.ShowAll:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.None];
                break;
            case InvoicesFilterState.Paid:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.Paid];
                break;
            // case InvoicesFilterState.PartiallyPaid:
            //     searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.PartiallyPaid];
            //     break;
            case InvoicesFilterState.Unpaid:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.Unpaid, InvoicePaidStatus.PartiallyPaid];
                break;
            default:
                throw new Error('Failed to process unknown invoices filter state.');
        }

        return searchCriteria;
    }

    private mapPastDueInvoicesToModel(
        pastDueInvoices: IGdnPastDueInvoiceDetailsEntity[]
    ): {
        invoices: InvoiceModel[];
        breakdownList: IInvoicePaymentScheduleEntity[];
    } {
        const invoices: InvoiceModel[] = [];
        const breakdownList: IInvoicePaymentScheduleEntity[] = [];

        pastDueInvoices.forEach(invoice => {
            // const paymentScheduleSum = invoice.PastDueInvoiceDetails?.reduce((sum, detail) => sum + (detail.CurrentAmountDue || 0), 0) || 0;

            const orderInvoice: Partial<OrderInvoice> = {
                Id: invoice.InvoiceId || '',
                InvoiceDate: invoice.InvoiceDate,
                TotalAmount: invoice.AmountDue,
                InvoiceDueDate: invoice.DueDate,
                CustomerAccountNumber: invoice.InvoiceAccount,
                InvoicePaidStatusValue: InvoicePaidStatus.PastDue,
                InvoiceTypeValue: invoice.InvoiceId?.startsWith('CN')
                    ? InvoiceType.CreditNoteInvoice
                    : invoice.InvoiceId?.startsWith('FTC')
                    ? InvoiceType.FreeTextCreditNoteInvoice
                    : InvoiceType.None,
                AmountBalance: invoice.InvoiceCurrentAmount
            };
            invoices.push(new InvoiceModel(orderInvoice as OrderInvoice));

            if (invoice.PastDueInvoiceDetails && invoice.PastDueInvoiceDetails.length > 0) {
                invoice.PastDueInvoiceDetails.forEach(detail => {
                    breakdownList.push({
                        Invoice: detail.InvoiceNumber,
                        AmountCur: detail.CurrentAmountDue,
                        DueDate: detail.DueDate,
                        Description: detail.Description,
                        SalesId: detail.SalesOrderNumber,
                        PurchaseOrder: detail.PONumber,
                        AllowableFreightCharge: detail.AllowableFreightCharge,
                        CashDiscDate: detail.CashDiscDate,
                        PossibleCashDisc: detail.PossbileCashDisc
                    });
                });
            }
        });

        return { invoices, breakdownList };
    }

    private async _loadPastDueInvoices(): Promise<void> {
        const { pastDuePageNumber, pastDuePageSize } = this.state;

        const resp = await getGdnPastDueInvoiceDetailsAsync(
            { callerContext: this.props.context.actionContext },
            [],
            pastDuePageNumber,
            pastDuePageSize
        );

        if (resp && resp.PastDueInvoices) {
            const { invoices, breakdownList } = this.mapPastDueInvoicesToModel(resp.PastDueInvoices);

            this.setState(prev => ({
                invoices: prev.invoices.concat(invoices),
                isLoading: false,
                isPageLoaded: true,
                invoiceBreakdownList: [...prev.invoiceBreakdownList, ...breakdownList],
                hasMorePastDueInvoices: resp.HasMoreRecords
            }));
        }
    }

    private async _handleFilterStateChange(state: InvoicesFilterState): Promise<void> {
        this.setState({
            invoicesFilterState: state,
            isLoading: true,
            isPageLoaded: false,
            invoiceBreakdownList: [],
            invoices: [],
            pastDuePageNumber: 1,
            hasMorePastDueInvoices: true
        });

        if (state === InvoicesFilterState.PastDue) {
            await this._loadPastDueInvoices();
        } else {
            const searchCriteria = await this._getInvoicesSearchCriteria(state);

            if (this._queryResultSettings.Paging) {
                this._queryResultSettings.Paging.Skip = 0;
            }

            const invoices = await getGdnInvoices(
                new GetGdnInvoicesInput(searchCriteria, this._queryResultSettings, this.getInvoiceType),
                this.props.context.actionContext
            );
            this._initializeWithInvoices(invoices);
        }
    }

    private _renderInvoicesFilter(className: string): JSX.Element {
        const containerClass = `${this._className}__container`;
        const containerContentClass = `${containerClass}__content`;
        const filterClassName = `${className}__filter`;
        return (
            <div>
                <div className={containerContentClass}>{this._renderItemsInCartMessage()}</div>
                {(this.props.config.displayInvoicePaymentTotals || false) && this.state.invoicePaymentTotals && (
                    <InvoicePaymentTotals
                        config={this.props.config}
                        resources={this.props.resources}
                        cultureFormatter={this.props.context.cultureFormatter}
                        invoicePaymentTotals={this.state.invoicePaymentTotals}
                    />
                )}
                <InvoicesFilterComponent
                    id={filterClassName}
                    typeName={this.props.typeName}
                    key={filterClassName}
                    className={filterClassName}
                    context={this.props.context}
                    resources={this.props.resources}
                    data={{}}
                    currentState={InvoicesFilterState.ShowAll}
                    onFilterStateChanged={this._handleFilterStateChange}
                    disabled={this.state.isLoading}
                />
            </div>
        );
    }

    private _renderInvoicesList(className: string): JSX.Element {
        const tableClassName = `${className}__table`;
        return (
            <InvoicesTableComponent
                invoiceButtonReferences={this.buttonChildReference}
                payInvoiceButtonReferences={this.buttonChildPayButtonReference}
                id={tableClassName}
                typeName={this.props.typeName}
                key={tableClassName}
                className={tableClassName}
                data={{ invoices: this.state.invoices, customer: this.props.data.customer.result! }}
                context={this.props.context}
                resources={this.props.resources}
                onChange={this._updateInvoicesState}
                onError={this._onAddToCartFailed}
                onRequestInvoice={this._requestSpecificInvoice}
                invoiceBreakdown={this.state.invoiceBreakdownList}
            />
        );
    }

    private _onAddToCartFailed(newErrorState: IInvoiceErrorState, reference?: React.RefObject<HTMLButtonElement>): void {
        const errorState = { ...newErrorState };
        errorState.errorHost = 'ADDINVOICETOCART';
        errorState.errorMessage =
            newErrorState.errorReason?.failureReason === 'EMPTYINPUT'
                ? this.props.resources.invoiceErrorInvoiceIdNotExist
                : this.props.resources.invoiceErrorFailedAddInvoiceToCart;

        const shouldShowAdditionalInfoInPopup =
            errorState.errorCode === 'Microsoft_Dynamics_Commerce_Runtime_InvoiceLineNotAllowedWithItemLines';
        this.setState({ errorState, payInvoiceErrorModalOpen: shouldShowAdditionalInfoInPopup, selectedButtonElement: reference?.current });
    }

    private _renderMakePayment(className: string, disabled: boolean): JSX.Element {
        const id = `${className}__make-payment`;
        const makePaymentResources: IMakePaymentResources = {
            makePaymentButtonText: this.props.resources.makePaymentButtonText,
            makePaymentButtonAriaLabel: this.props.resources.makePaymentButtonAriaLabel
        };

        const selectedInvoices = this._getSelectedInvoices();

        return (
            <MakePaymentComponent
                id={id}
                typeName={this.props.typeName}
                key={id}
                data={{ invoices: selectedInvoices }}
                context={this.props.context}
                telemetry={this.props.telemetry}
                moduleId={this.props.id}
                moduleTypeName={this.props.typeName}
                navigationUrl={getUrlSync('cart', this.props.context.actionContext)}
                resources={makePaymentResources}
                onError={this._onAddToCartFailed}
                buttonReferences={this.makePaymentReference}
                disabled={disabled}
            />
        );
    }

    private _setPayInvoiceErrorModalClose(): void {
        this.setState({
            payInvoiceErrorModalOpen: false
        });
        this.state.selectedButtonElement?.focus();
    }

    private _renderInvoiceErrors(): JSX.Element {
        const {
            resources: {
                payInvoiceErrorDialogHeader,
                payInvoiceErrorDialogBodyErrorTitle,
                payInvoiceErrorDialogBodyErrorDescription,
                payInvoiceErrorDialogFooterGoToCartButtonText,
                payInvoiceErrorDialogFooterCancelButtonText
            }
        } = this.props;

        const cartUrl = getUrlSync('cart', this.props.context.actionContext) || '';

        const errorClassName = `${this._className}__errors`;

        return (
            <InvoiceErrorComponent
                id={errorClassName}
                typeName={this.props.typeName}
                data={{}}
                messageHeader={this.props.resources.invoiceErrorMessageHeader}
                context={this.props.context}
                errorMessage={this.state.errorState.errorMessage}
                otherError={this.state.errorState.otherError}
                showError={this.state.errorState.errorHost === 'ADDINVOICETOCART'}
                payInvoiceErrorModalOpen={this.state.payInvoiceErrorModalOpen}
                errorDialogHeader={payInvoiceErrorDialogHeader}
                errorDialogBodyErrorTitle={payInvoiceErrorDialogBodyErrorTitle}
                errorDialogBodyErrorDescription={payInvoiceErrorDialogBodyErrorDescription}
                errorDialogFooterGoToCartButtonText={payInvoiceErrorDialogFooterGoToCartButtonText}
                errorDialogFooterCancelButtonText={payInvoiceErrorDialogFooterCancelButtonText}
                cartUrl={cartUrl}
                setPayInvoiceErrorModalClose={this._setPayInvoiceErrorModalClose}
            />
        );
    }

    private _renderItemsInCartMessage(): JSX.Element {
        const cart = this.props.data.cart?.result;

        const message = cart?.hasInvoiceLine
            ? this.props.resources.invoiceInCartPaymentMessage
            : this.props.config.partsInCartPaymentMessage;

        // parts or invoice in cart
        if (!cart?.isEmpty && message) {
            return (
                <Alert className='msc-alert-invoice' isOpen={this.state.isOpen}>
                    <div className='msc-alert-invoice__header' aria-hidden='true'>
                        <span className='msi-exclamation-triangle' />
                        <span>{message}</span>
                        <button className='msc-alert-invoice-toggle' onClick={this._onDismiss}>
                            &#10005;
                        </button>
                    </div>
                </Alert>
            );
        }
        return <></>;
    }

    // private _renderInvoiceInCartPaymentMessage(): JSX.Element {
    //     const cart = this.props.data.cart?.result;

    //     if (!cart?.hasInvoiceLine) {
    //         return <></>;
    //     }

    //     return (
    //         <Alert className='msc-alert-invoice' isOpen={this.state.isOpen}>
    //             <div className='msc-alert-invoice__header' aria-hidden='true'>
    //                 <span className='msi-exclamation-triangle' />
    //                 <span>{this.props.resources.invoiceInCartPaymentMessage}</span>
    //                 <button className='msc-alert-invoice-toggle' onClick={this._onDismiss}>
    //                     &#10005;
    //                 </button>
    //             </div>
    //         </Alert>
    //     );
    // }

    // Dismiss alert.
    private readonly _onDismiss = () => {
        this.setState({
            isOpen: false
        });
    };

    private _renderPagination(): JSX.Element | undefined {
        const { data, resources } = this.props;
        if (this.state.invoicesFilterState !== InvoicesFilterState.PastDue) {
            const invoices = data?.invoices?.result;
            if (!invoices || invoices.length < this._queryResultSettings.Paging!.Top!) {
                return undefined;
            }
        } else {
            if (!this.state.hasMorePastDueInvoices || this.state.invoices.length === 0) {
                return undefined;
            }
        }

        return (
            <Button
                className={classnames(`${this._className}__load-more`, this.state.isLoading ? 'is-busy' : '')}
                disabled={this.state.isLoading}
                onClick={this._loadMoreInvoices}
            >
                {resources.invoicesLoadMore}
            </Button>
        );
    }

    private async _loadMoreInvoices(): Promise<void> {
        this.setState({ isLoading: true });
        if (this.state.invoicesFilterState === InvoicesFilterState.PastDue) {
            this.setState(
                prev => ({ pastDuePageNumber: prev.pastDuePageNumber + 1 }),
                async () => {
                    await this._loadPastDueInvoices();
                }
            );
        } else {
            const searchCriteria = await this._getInvoicesSearchCriteria(this.state.invoicesFilterState);
            if (this._queryResultSettings.Paging) {
                this._queryResultSettings.Paging.Skip! += this._queryResultSettings.Paging.Top!;
            }
            const newInvoices = await getGdnInvoices(
                new GetGdnInvoicesInput(searchCriteria, this._queryResultSettings, this.getInvoiceType),
                this.props.context.actionContext
            );
            this._addInvoices(newInvoices);

            // ticket 8686
            if (this.props.config.renderListView) {
                const newInvoiceModels = newInvoices.map((invoice, index) => {
                    this.buttonChildReference[Number(index)] = React.createRef<HTMLButtonElement>();
                    this.buttonChildPayButtonReference[Number(index)] = React.createRef<HTMLButtonElement>();
                    return new InvoiceModel(invoice);
                });

                // get new invoices to load
                const invoicesToLoad = newInvoiceModels.filter(
                    inv => !this.state.invoiceBreakdownList.some(breakdown => breakdown.Invoice === inv.id)
                );
                const ids = invoicesToLoad.map(inv => inv.id);
                const input = new GetInvoiceBreakdownInput(ids);

                // get breakdown list for paid and unpaid invoices
                const invoiceBreakdownList = await getInvoiceBreakdownAction(input, this.props.context.actionContext);
                this.setState(prev => ({
                    invoiceBreakdownList: [...prev.invoiceBreakdownList, ...invoiceBreakdownList.invoiceBreakdownList]
                }));
            }
        }
    }

    private _renderInvoiceRequestModal(): JSX.Element {
        const customer = this.props.data.customer.result;
        const {
            invoiceRequestModalCancelLabel,
            invoiceRequestModalSubmitLabel,
            invoiceRequestModalHeaderLabel,
            invoiceRequestModalSendToEmailLabel,
            invoiceRequestModalSearch,
            invoiceRequestModalInvoiceSearchDisplay,
            invoiceRequestModalSingleInvoiceSearchDisplay,
            invoiceRequestModalSearchButton
        } = this.props.resources;

        return (
            <RequestInvoiceModal
                className={this._className}
                email={customer?.Email || ''}
                isModalOpen={this.state.isInvoiceRequestModalOpen}
                toggle={this._onToggleInvoiceRequestModal}
                cancelLabel={invoiceRequestModalCancelLabel}
                modalHeaderLabel={invoiceRequestModalHeaderLabel}
                sendToEmailLabel={invoiceRequestModalSendToEmailLabel}
                submitRequestLabel={invoiceRequestModalSubmitLabel}
                onSubmitRequest={this._requestInvoice}
                invoices={this.state.invoices}
                searchPlaceholderLabel={invoiceRequestModalSearch}
                invoicesFoundString={invoiceRequestModalInvoiceSearchDisplay}
                singleInvoiceFoundString={invoiceRequestModalSingleInvoiceSearchDisplay}
                searchButtonAltText={invoiceRequestModalSearchButton}
                resources={this.props.resources}
            />
        );
    }

    private _onToggleInvoiceRequestModal(): void {
        this.setState({
            isInvoiceRequestModalOpen: !this.state.isInvoiceRequestModalOpen
        });
        if (this.state.isInvoiceRequestModalOpen) {
            this.state.selectedButtonElement?.focus();
        }
    }

    /**
     * Request specific invoice function.
     */
    private readonly _onToggleInvoiceRequestParentModal = () => {
        this.setState(
            {
                selectedButtonElement: this.payInvoiceRequestReference.current
            },
            () => {
                this._onToggleInvoiceRequestModal();
            }
        );
    };

    private _renderInvoiceRequestButton(): JSX.Element | null {
        const { invoiceRequestButtonLabel } = this.props.resources;
        const customer = this.props.data.customer.result;

        if (StringExtensions.isNullOrWhitespace(customer?.Email)) {
            return null;
        }

        return (
            <RequestInvoiceButton
                innerReference={this.payInvoiceRequestReference}
                requestInvoiceButtonLabel={invoiceRequestButtonLabel}
                onClicked={this._onToggleInvoiceRequestParentModal}
                className={this._className}
            />
        );
    }

    private _requestInvoice(invoiceId: string): void {
        this.invoiceIds.push(invoiceId);
        this._onToggleInvoiceRequestModal();
    }

    private _getSelectedInvoices(): InvoiceModel[] {
        let selectedInvoices = this.state.invoices.filter(invoice => invoice.isSelected);
        if (!ArrayExtensions.hasElements(selectedInvoices)) {
            selectedInvoices = this.state.invoices;
        }

        return selectedInvoices;
    }

    private _requestSpecificInvoice(
        specificInvoice: InvoiceModel,
        requestInvoiceButtonReference: React.RefObject<HTMLButtonElement>
    ): void {
        this.state.invoices.forEach((invoice: InvoiceModel) => {
            if (invoice === specificInvoice) {
                invoice.isSelected = true;
            } else {
                invoice.isSelected = false;
            }
        });
        this.setState({
            selectedButtonElement: requestInvoiceButtonReference.current
        });
        this._onToggleInvoiceRequestModal();
    }
}

export default InvoicesList;
